import { supabase } from '@/features/sistema/services/supabaseClient';

interface DocumentContent {
  fileName: string;
  type: 'pdf' | 'excel' | 'word' | 'unknown';
  content: string;
  structured?: {
    cronograma?: CronogramaData[];
    orcamento?: OrcamentoData[];
    resumoOrcamento?: ResumoOrcamento;
    resumoCronograma?: ResumoCronograma;
  };
}

interface CronogramaData {
  atividade: string;
  dataInicio: string;
  dataFim: string;
  duracao?: string;
  responsavel?: string;
  progresso?: string;
  categoria?: string;
}

interface OrcamentoData {
  item: string;
  descricao: string;
  unidade?: string;
  quantidade?: number;
  valorUnitario?: number;
  valorMaterial?: number;
  valorMaoObra?: number;
  valorTotal?: number;
  categoria?: string;
}

interface ResumoOrcamento {
  totalGeral: number;
  categorias: {
    [categoria: string]: {
      quantidade: number;
      valorMaterial: number;
      valorMaoObra: number;
      valorTotal: number;
      itens: number;
      detalhes: Array<{
        codigo: string;
        descricao: string;
        quantidade?: number;
        valorTotal?: number;
        unidade?: string;
      }>;
    };
  };
}

interface ResumoCronograma {
  totalAtividades: number;
  periodoExecucao: {
    inicio: string;
    fim: string;
    duracaoTotal: number;
  };
  fases: {
    [fase: string]: {
      atividades: number;
      inicio: string;
      fim: string;
    };
  };
}

 
class FileReaderService {
  
  /**
   * Lê o conteúdo de um documento da obra
   */
  async readDocument(documento: any): Promise<DocumentContent | null> {
    try {
      console.log('📄 Lendo documento:', documento.nome_arquivo);

      // Baixar arquivo do Supabase Storage
      const fileData = await this.downloadFile(documento.caminho_storage);
      if (!fileData) {
        console.error('❌ Não foi possível baixar o arquivo');
        return null;
      }

      // Identificar tipo e processar
      const tipo = this.getFileType(documento.tipo_arquivo, documento.nome_arquivo);
      
      switch (tipo) {
        case 'pdf':
          return await this.readPDFBrowser(fileData, documento.nome_arquivo);
        case 'excel':
          return await this.readExcelAdvanced(fileData, documento.nome_arquivo); // ✅ NOVA FUNÇÃO
        case 'word':
          return await this.readWord(fileData, documento.nome_arquivo);
        default:
          console.warn('⚠️ Tipo de arquivo não suportado:', documento.tipo_arquivo);
          return null;
      }

    } catch (error) {
      console.error('❌ Erro ao ler documento:', error);
      return null;
    }
  }

  /**
   * Lê múltiplos documentos da obra
   */
  async readObraDocuments(documentos: any[]): Promise<DocumentContent[]> {
    const results: DocumentContent[] = [];
    for (const doc of documentos) {
      const content = await this.readDocument(doc);
      if (content) results.push(content);
    }
    return results;
  }

  /**
   * Baixa arquivo do Supabase Storage
   */
  private async downloadFile(caminhoStorage: string): Promise<ArrayBuffer | null> {
    try {
      const { data, error } = await supabase.storage
        .from('documentos')
        .download(caminhoStorage);

      if (error) {
        console.error('❌ Erro ao baixar arquivo:', error);
        return null;
      }

      return await data.arrayBuffer();
    } catch (error) {
      console.error('❌ Erro no download:', error);
      return null;
    }
  }

  /**
   * Identifica tipo do arquivo
   */
  private getFileType(mimeType: string, fileName: string): 'pdf' | 'excel' | 'word' | 'unknown' {
    const extension = fileName.toLowerCase().split('.').pop();
    
    if (mimeType?.includes('pdf') || extension === 'pdf') {
      return 'pdf';
    }
    
    if (mimeType?.includes('spreadsheet') || 
        mimeType?.includes('excel') ||
        ['xlsx', 'xls'].includes(extension || '')) {
      return 'excel';
    }
    
    if (mimeType?.includes('document') || 
        mimeType?.includes('word') ||
        ['docx', 'doc'].includes(extension || '')) {
      return 'word';
    }
    
    return 'unknown';
  }

  /**
   * Lê conteúdo de PDF usando PDF.js (BROWSER-COMPATIBLE)
   */
  private async readPDFBrowser(fileData: ArrayBuffer, fileName: string): Promise<DocumentContent> {
    try {
      console.log('📄 Lendo PDF com PDF.js:', fileName);

      // Usar PDF.js via CDN (compatível com browser)
      const pdfjsLib = await this.loadPDFJS();
      
      // Converter ArrayBuffer para Uint8Array
      const pdfData = new Uint8Array(fileData);
      
      // Carregar documento PDF
      const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
      console.log('📊 PDF carregado:', fileName, 'páginas:', pdf.numPages);

      let fullText = '';
      
      // Extrair texto de todas as páginas (limitado a 5 páginas para performance)
      const maxPages = Math.min(pdf.numPages, 5);
      
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          
          const pageText = textContent.items
            .map((item: any) => item.str)
            .join(' ');
          
          fullText += `\n=== PÁGINA ${pageNum} ===\n${pageText}\n`;
          
        } catch (pageError) {
          console.warn(`⚠️ Erro ao ler página ${pageNum}:`, pageError);
        }
      }

      console.log('✅ PDF processado:', fileName, 'caracteres:', fullText.length);

      // ✅ VERIFICAR SE É PDF COM POUCO TEXTO (Gantt, imagens)
      if (fullText.trim().length < 100) {
        console.warn('⚠️ PDF com pouco texto detectado (Gantt/imagem):', fullText.length, 'caracteres');
        
        return {
          fileName,
          type: 'pdf',
          content: `📄 **${fileName}**

⚠️ **PDF IDENTIFICADO COMO CRONOGRAMA VISUAL (GANTT)**

Este arquivo contém principalmente elementos gráficos (gráficos Gantt, tabelas como imagem).

**Para análise manual:**
• Abra o arquivo diretamente para visualizar o cronograma
• Verifique atividades, datas e marcos visuais
• Consulte responsáveis indicados no documento

**SUGESTÃO ACOR:**
• Exporte o cronograma também em Excel (.xlsx)
• Isso permitirá análise automática completa
• Mantenha os dados sempre atualizados

**Valores ACOR:**
🏃‍♂️ **Agilidade**: Consulta rápida por arquivo Excel
📋 **Organização**: Dados estruturados para análise
🎯 **Resultado**: Informações precisas para decisões`,
          structured: {
            cronograma: [],
            orcamento: [],
            resumoOrcamento: { 
              totalGeral: 0, 
              categorias: {} 
            },
            resumoCronograma: { 
              totalAtividades: 0, 
              periodoExecucao: { inicio: '', fim: '', duracaoTotal: 0 }, 
              fases: {} 
            }
          }
        };
      }

      // ✅ PARSER INTELIGENTE: extrair dados estruturados
      const cronogramaData = this.extractCronogramaFromText(fullText);
      const orcamentoData = this.extractOrcamentoFromText(fullText);
      
      // ✅ AGREGAÇÃO: criar resumos estruturados
      const resumoOrcamento = this.createResumoOrcamento(orcamentoData);
      const resumoCronograma = this.createResumoCronograma(cronogramaData);

      return {
        fileName,
        type: 'pdf',
        content: fullText,
        structured: {
          cronograma: cronogramaData,
          orcamento: orcamentoData,
          resumoOrcamento,
          resumoCronograma
        }
      };

    } catch (error) {
      
      // Fallback: retornar informação básica
      return {
        fileName,
        type: 'pdf',
        content: `Arquivo PDF: ${fileName}. Conteúdo detectado mas não extraído completamente. 
        
ANÁLISE MANUAL RECOMENDADA:
• Abra o arquivo diretamente para ver cronograma completo
• Verifique datas e atividades específicas
• Consulte responsáveis indicados no documento

INFORMAÇÕES GERAIS DO ARQUIVO:
• Nome: ${fileName}
• Tipo: Documento PDF
• Status: Disponível no sistema`
      };
    }
  }

  /**
   * 🆕 NOVA: Lê conteúdo de Excel com parsing avançado para ACOR
   */
  private async readExcelAdvanced(fileData: ArrayBuffer, fileName: string): Promise<DocumentContent> {
    try {
      console.log('📊 Lendo Excel avançado ACOR:', fileName);

      // Carregar SheetJS dinamicamente
      const XLSX = await this.loadXLSX();
      const workbook = XLSX.read(fileData, { 
        type: 'array',
        cellDates: true,    // Converter datas automaticamente
        cellNF: false,      // Não formatar números
        cellStyles: false   // Não precisamos de estilos
      });

      console.log('📋 Planilhas encontradas:', workbook.SheetNames);

      let fullContent = `📊 **ARQUIVO EXCEL: ${fileName}**\n\n`;
      let allOrcamentoData: OrcamentoData[] = [];
      let allCronogramaData: CronogramaData[] = [];

      // 🎯 PROCESSAR CADA PLANILHA
      for (const sheetName of workbook.SheetNames) {
        console.log(`📄 Processando planilha: ${sheetName}`);
        
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        
        // Identificar tipo da planilha
        const sheetType = this.identifyExcelSheetType(sheetName, data);
        
        fullContent += `\n=== 📊 PLANILHA: ${sheetName.toUpperCase()} ===\n`;
        fullContent += `🏷️ Tipo: ${sheetType.toUpperCase()}\n`;
        fullContent += `📋 Linhas: ${data.length}\n\n`;

        if (sheetType === 'orcamento') {
          // 💰 PARSER DE ORÇAMENTO
          const orcamentoSheet = this.parseOrcamentoExcel(data, sheetName);
          allOrcamentoData.push(...orcamentoSheet);
          fullContent += `💰 ${orcamentoSheet.length} itens de orçamento extraídos\n`;
          
          // Mostrar resumo dos primeiros itens
          orcamentoSheet.slice(0, 5).forEach(item => {
            if (item.valorTotal && item.valorTotal > 0) {
              fullContent += `  • ${item.item} - ${item.descricao}: R$ ${item.valorTotal.toLocaleString('pt-BR')}\n`;
            }
          });
          if (orcamentoSheet.length > 5) {
            fullContent += `  ... e mais ${orcamentoSheet.length - 5} itens\n`;
          }
          
        } else if (sheetType === 'cronograma') {
          // 📅 PARSER DE CRONOGRAMA
          const cronogramaSheet = this.parseCronogramaExcel(data, sheetName);
          allCronogramaData.push(...cronogramaSheet);
          fullContent += `📅 ${cronogramaSheet.length} atividades extraídas\n`;
          
          // Mostrar resumo das primeiras atividades
          cronogramaSheet.slice(0, 3).forEach(ativ => {
            fullContent += `  • ${ativ.atividade}`;
            if (ativ.dataInicio) fullContent += ` (${ativ.dataInicio}`;
            if (ativ.dataFim && ativ.dataFim !== ativ.dataInicio) fullContent += ` - ${ativ.dataFim}`;
            if (ativ.dataInicio) fullContent += `)`;
            fullContent += `\n`;
          });
          if (cronogramaSheet.length > 3) {
            fullContent += `  ... e mais ${cronogramaSheet.length - 3} atividades\n`;
          }
          
        } else {
          // 📋 PLANILHA GENÉRICA - mostrar preview
          fullContent += `📋 Planilha genérica - primeiras linhas:\n`;
          data.slice(0, 5).forEach((row: any, idx: number) => {
            const rowStr = Array.isArray(row) ? row.slice(0, 5).join(' | ') : String(row);
            fullContent += `  ${idx + 1}: ${rowStr}\n`;
          });
          if (data.length > 5) {
            fullContent += `  ... e mais ${data.length - 5} linhas\n`;
          }
        }
        
        fullContent += '\n';
      }

      // 🎯 GERAR RESUMOS ESTRUTURADOS
      const resumoOrcamento = this.createResumoOrcamento(allOrcamentoData);
      const resumoCronograma = this.createResumoCronograma(allCronogramaData);

      // 📊 ADICIONAR RESUMO EXECUTIVO
      if (allOrcamentoData.length > 0) {
        fullContent += `\n💰 **RESUMO DE ORÇAMENTO CONSOLIDADO**\n`;
        fullContent += `📋 Total de itens: ${allOrcamentoData.length}\n`;
        fullContent += `💵 Valor total: R$ ${resumoOrcamento.totalGeral.toLocaleString('pt-BR')}\n`;
        
        const categorias = Object.entries(resumoOrcamento.categorias)
          .sort(([,a], [,b]) => b.valorTotal - a.valorTotal)
          .slice(0, 5);
          
        fullContent += `🏗️ Principais categorias:\n`;
        categorias.forEach(([cat, dados]) => {
          const percentual = ((dados.valorTotal / resumoOrcamento.totalGeral) * 100).toFixed(1);
          fullContent += `  • ${cat.replace('_', ' ').toUpperCase()}: R$ ${dados.valorTotal.toLocaleString('pt-BR')} (${percentual}%)\n`;
        });
        fullContent += '\n';
      }

      if (allCronogramaData.length > 0) {
        fullContent += `\n📅 **RESUMO DE CRONOGRAMA CONSOLIDADO**\n`;
        fullContent += `📋 Total de atividades: ${resumoCronograma.totalAtividades}\n`;
        if (resumoCronograma.periodoExecucao.inicio) {
          fullContent += `📆 Período: ${resumoCronograma.periodoExecucao.inicio} até ${resumoCronograma.periodoExecucao.fim}\n`;
          fullContent += `⏱️ Duração total: ${resumoCronograma.periodoExecucao.duracaoTotal} dias\n`;
        }
        
        if (Object.keys(resumoCronograma.fases).length > 0) {
          fullContent += `🏗️ Fases identificadas:\n`;
          Object.entries(resumoCronograma.fases).forEach(([fase, dados]) => {
            fullContent += `  • ${fase}: ${dados.atividades} atividades\n`;
          });
        }
        fullContent += '\n';
      }

      console.log('✅ Excel processado:', fileName, {
        orcamento: allOrcamentoData.length,
        cronograma: allCronogramaData.length,
        valorTotal: resumoOrcamento.totalGeral
      });

      return {
        fileName,
        type: 'excel',
        content: fullContent,
        structured: {
          cronograma: allCronogramaData,
          orcamento: allOrcamentoData,
          resumoOrcamento,
          resumoCronograma
        }
      };

    } catch (error) {
      console.error('❌ Erro ao processar Excel:', error);
      
      // Fallback: usar método simples
      return await this.readExcelSimple(fileData, fileName);
    }
  }

  /**
   * 🆕 IDENTIFICA TIPO DA PLANILHA EXCEL
   */
  private identifyExcelSheetType(sheetName: string, data: any[]): 'orcamento' | 'cronograma' | 'generic' {
    const sheetNameLower = sheetName.toLowerCase();
    
    // 🎯 IDENTIFICAÇÃO POR NOME DA PLANILHA
    if (sheetNameLower.includes('orçamento') || 
        sheetNameLower.includes('orcamento') ||
        sheetNameLower.includes('custo') ||
        sheetNameLower.includes('valor') ||
        sheetNameLower.includes('preço')) {
      return 'orcamento';
    }
    
    if (sheetNameLower.includes('cronograma') ||
        sheetNameLower.includes('prazo') ||
        sheetNameLower.includes('agenda') ||
        sheetNameLower.includes('gantt') ||
        sheetNameLower.includes('atividade')) {
      return 'cronograma';
    }
    
    // 🎯 IDENTIFICAÇÃO POR CONTEÚDO (primeiras linhas)
    const headerText = data.slice(0, 5)
      .map((row: any[]) => Array.isArray(row) ? row.join(' ').toLowerCase() : '')
      .join(' ');
    
    // Padrões de orçamento
    const orcamentoKeywords = [
      'código', 'descrição', 'quantidade', 'valor', 'total', 'material', 'mão de obra',
      'unidade', 'preço', 'custo', 'item', 'serviço'
    ];
    
    // Padrões de cronograma
    const cronogramaKeywords = [
      'atividade', 'início', 'fim', 'duração', 'responsável', 'prazo',
      'data', 'tarefa', 'marco', 'progresso', 'status'
    ];
    
    const orcamentoScore = orcamentoKeywords.reduce((score, keyword) => {
      return score + (headerText.includes(keyword) ? 1 : 0);
    }, 0);
    
    const cronogramaScore = cronogramaKeywords.reduce((score, keyword) => {
      return score + (headerText.includes(keyword) ? 1 : 0);
    }, 0);
    
    if (orcamentoScore >= 3) return 'orcamento';
    if (cronogramaScore >= 2) return 'cronograma';
    
    return 'generic';
  }

  /**
   * 🆕 PARSER DE ORÇAMENTO EXCEL AVANÇADO
   */
  private parseOrcamentoExcel(data: any[], sheetName: string): OrcamentoData[] {
    const orcamento: OrcamentoData[] = [];
    
    console.log(`💰 Analisando orçamento na planilha: ${sheetName}`);
    console.log(`📋 Total de linhas: ${data.length}`);
    
    if (data.length < 2) {
      console.warn('⚠️ Planilha muito pequena para análise');
      return orcamento;
    }

    // 🎯 ENCONTRAR HEADER (linha com colunas)
    let headerRowIndex = -1;
    let columnMap: { [key: string]: number } = {};
    
    for (let i = 0; i < Math.min(data.length, 10); i++) {
      const row = data[i];
      if (!Array.isArray(row)) continue;
      
      const rowStr = row.join('|').toLowerCase();
      
      // Procurar por indicadores de header de orçamento
      if (rowStr.includes('código') || 
          rowStr.includes('descrição') || 
          (rowStr.includes('valor') && rowStr.includes('total'))) {
        
        headerRowIndex = i;
        console.log(`📋 Header encontrado na linha ${i + 1}`);
        
        // Mapear colunas
        row.forEach((cell: any, colIndex: number) => {
          const cellStr = String(cell || '').toLowerCase().trim();
          
          // Mapeamento de colunas
          if (cellStr.includes('código') || cellStr.includes('item')) columnMap.codigo = colIndex;
          if (cellStr.includes('descrição') || cellStr.includes('descricao') || cellStr.includes('serviço')) columnMap.descricao = colIndex;
          if (cellStr.includes('unidade') || cellStr.includes('un')) columnMap.unidade = colIndex;
          if (cellStr.includes('quantidade') || cellStr.includes('qtd')) columnMap.quantidade = colIndex;
          if (cellStr.includes('valor') && (cellStr.includes('unitário') || cellStr.includes('unitario'))) columnMap.valorUnitario = colIndex;
          if (cellStr.includes('material') && cellStr.includes('total')) columnMap.valorMaterial = colIndex;
          if ((cellStr.includes('mão') || cellStr.includes('mao')) && cellStr.includes('obra')) columnMap.valorMaoObra = colIndex;
          if ((cellStr.includes('valor') || cellStr.includes('preço')) && cellStr.includes('total')) columnMap.valorTotal = colIndex;
        });
        
        break;
      }
    }
    
    console.log('🗺️ Mapeamento de colunas:', columnMap);
    
    if (headerRowIndex === -1 || !columnMap.descricao) {
      console.warn('⚠️ Header não encontrado ou incompleto, tentando análise automática...');
      return this.parseOrcamentoExcelAutomatico(data);
    }

    // 🎯 PROCESSAR LINHAS DE DADOS
    let itensProcessados = 0;
    
    for (let i = headerRowIndex + 1; i < data.length; i++) {
      const row = data[i];
      if (!Array.isArray(row)) continue;
      
      // Verificar se linha tem dados significativos
      const codigo = this.getCellValue(row, columnMap.codigo);
      const descricao = this.getCellValue(row, columnMap.descricao);
      const valorTotal = this.parseNumberExcel(this.getCellValue(row, columnMap.valorTotal));
      
      // Pular linhas vazias ou de total
      if (!descricao || 
          descricao.toLowerCase().includes('total') ||
          descricao.toLowerCase().includes('subtotal') ||
          descricao.trim().length < 3) {
        continue;
      }
      
      const quantidade = this.parseNumberExcel(this.getCellValue(row, columnMap.quantidade));
      const valorUnitario = this.parseNumberExcel(this.getCellValue(row, columnMap.valorUnitario));
      const valorMaterial = this.parseNumberExcel(this.getCellValue(row, columnMap.valorMaterial));
      const valorMaoObra = this.parseNumberExcel(this.getCellValue(row, columnMap.valorMaoObra));
      const unidade = this.getCellValue(row, columnMap.unidade) || 'unid';
      
      // Só adicionar se tiver valor total significativo
      if (valorTotal && valorTotal > 0) {
        const categoria = this.categorizarItemACOR(codigo, descricao);
        
        const item: OrcamentoData = {
          item: codigo || `Item ${itensProcessados + 1}`,
          descricao: descricao,
          unidade: unidade,
          quantidade: quantidade || 0,
          valorUnitario: valorUnitario || 0,
          valorMaterial: valorMaterial || 0,
          valorMaoObra: valorMaoObra || 0,
          valorTotal: valorTotal,
          categoria: categoria
        };
        
        orcamento.push(item);
        itensProcessados++;
        
        if (itensProcessados <= 5) {
          console.log(`✅ Item ${itensProcessados}: ${codigo} - ${descricao} = R$ ${valorTotal.toLocaleString('pt-BR')} [${categoria}]`);
        }
      }
    }
    
    console.log(`📊 Total de itens processados: ${itensProcessados}`);
    return orcamento;
  }

  /**
   * 🆕 PARSER AUTOMÁTICO quando header não é encontrado
   */
  private parseOrcamentoExcelAutomatico(data: any[]): OrcamentoData[] {
    const orcamento: OrcamentoData[] = [];
    
    console.log('🤖 Iniciando análise automática de orçamento...');
    
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!Array.isArray(row) || row.length < 3) continue;
      
      // Procurar padrões de números que podem ser valores
      const numericCells = row.map((cell, index) => ({
        index,
        value: this.parseNumberExcel(cell),
        original: cell
      })).filter(cell => cell.value > 0);
      
      // Se tem poucos números, provavelmente não é linha de orçamento
      if (numericCells.length < 2) continue;
      
      // Procurar a célula com maior valor (provavelmente valor total)
      const valorTotalCell = numericCells.reduce((max, cell) => 
        cell.value > max.value ? cell : max
      );
      
      // Procurar descrição (primeira célula com texto significativo)
      let descricao = '';
      for (const cell of row) {
        const cellStr = String(cell || '').trim();
        if (cellStr.length > 5 && !this.isNumberLike(cellStr)) {
          descricao = cellStr;
          break;
        }
      }
      
      if (descricao && valorTotalCell.value > 100) { // Valor mínimo para filtrar ruído
        const categoria = this.categorizarItemACOR('', descricao);
        
        const item: OrcamentoData = {
          item: `Auto ${orcamento.length + 1}`,
          descricao: descricao,
          unidade: 'unid',
          quantidade: 1,
          valorUnitario: valorTotalCell.value,
          valorMaterial: 0,
          valorMaoObra: 0,
          valorTotal: valorTotalCell.value,
          categoria: categoria
        };
        
        orcamento.push(item);
        
        if (orcamento.length <= 3) {
          console.log(`🤖 Auto-detectado: ${descricao} = R$ ${valorTotalCell.value.toLocaleString('pt-BR')}`);
        }
      }
    }
    
    console.log(`🤖 Análise automática: ${orcamento.length} itens encontrados`);
    return orcamento;
  }

  /**
   * 🆕 PARSER DE CRONOGRAMA EXCEL AVANÇADO
   */
  private parseCronogramaExcel(data: any[], sheetName: string): CronogramaData[] {
    const cronograma: CronogramaData[] = [];
    
    console.log(`📅 Analisando cronograma na planilha: ${sheetName}`);
    
    if (data.length < 2) return cronograma;

    // 🎯 ENCONTRAR HEADER
    let headerRowIndex = -1;
    let columnMap: { [key: string]: number } = {};
    
    for (let i = 0; i < Math.min(data.length, 10); i++) {
      const row = data[i];
      if (!Array.isArray(row)) continue;
      
      const rowStr = row.join('|').toLowerCase();
      
      if (rowStr.includes('atividade') || 
          rowStr.includes('tarefa') ||
          (rowStr.includes('início') && rowStr.includes('fim'))) {
        
        headerRowIndex = i;
        console.log(`📋 Header cronograma encontrado na linha ${i + 1}`);
        
        // Mapear colunas
        row.forEach((cell: any, colIndex: number) => {
          const cellStr = String(cell || '').toLowerCase().trim();
          
          if (cellStr.includes('atividade') || cellStr.includes('tarefa') || cellStr.includes('descrição')) columnMap.atividade = colIndex;
          if (cellStr.includes('início') || cellStr.includes('inicio')) columnMap.dataInicio = colIndex;
          if (cellStr.includes('fim') || cellStr.includes('término') || cellStr.includes('termino')) columnMap.dataFim = colIndex;
          if (cellStr.includes('duração') || cellStr.includes('duracao')) columnMap.duracao = colIndex;
          if (cellStr.includes('responsável') || cellStr.includes('responsavel')) columnMap.responsavel = colIndex;
          if (cellStr.includes('progresso') || cellStr.includes('%')) columnMap.progresso = colIndex;
        });
        
        break;
      }
    }
    
    console.log('🗺️ Mapeamento cronograma:', columnMap);
    
    if (headerRowIndex === -1 || !columnMap.atividade) {
      console.warn('⚠️ Header de cronograma não encontrado');
      return cronograma;
    }

    // 🎯 PROCESSAR ATIVIDADES
    for (let i = headerRowIndex + 1; i < data.length; i++) {
      const row = data[i];
      if (!Array.isArray(row)) continue;
      
      const atividade = this.getCellValue(row, columnMap.atividade);
      
      // Pular linhas vazias
      if (!atividade || atividade.trim().length < 3) continue;
      
      const dataInicio = this.parseDateExcel(this.getCellValue(row, columnMap.dataInicio));
      const dataFim = this.parseDateExcel(this.getCellValue(row, columnMap.dataFim));
      const duracao = this.getCellValue(row, columnMap.duracao);
      const responsavel = this.getCellValue(row, columnMap.responsavel);
      const progresso = this.getCellValue(row, columnMap.progresso);
      
      const item: CronogramaData = {
        atividade: atividade,
        dataInicio: dataInicio,
        dataFim: dataFim,
        duracao: duracao || '',
        responsavel: responsavel || '',
        progresso: progresso || '',
        categoria: this.getCategoriaAtividade(atividade)
      };
      
      cronograma.push(item);
      
      if (cronograma.length <= 3) {
        console.log(`📅 Atividade: ${atividade} (${dataInicio} - ${dataFim})`);
      }
    }
    
    console.log(`📊 Total de atividades processadas: ${cronograma.length}`);
    return cronograma;
  }

  /**
   * 🆕 HELPERS para Excel
   */
  private getCellValue(row: any[], columnIndex: number | undefined): string {
    if (columnIndex === undefined || columnIndex < 0 || columnIndex >= row.length) {
      return '';
    }
    return String(row[columnIndex] || '').trim();
  }

  private parseNumberExcel(value: any): number {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    
    const str = String(value).trim();
    if (!str) return 0;
    
    // Remover caracteres não numéricos exceto . , -
    const cleaned = str
      .replace(/[R$\s]/g, '')  // Remover R$ e espaços
      .replace(/\./g, '')      // Remover pontos (milhares)
      .replace(',', '.');      // Converter vírgula para ponto decimal
    
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }

  private parseDateExcel(value: any): string {
    if (!value) return '';
    
    // Se já é uma data JS
    if (value instanceof Date) {
      return value.toLocaleDateString('pt-BR');
    }
    
    // Se é um número (data serial do Excel)
    if (typeof value === 'number') {
      const date = new Date((value - 25569) * 86400 * 1000);
      return date.toLocaleDateString('pt-BR');
    }
    
    // Se é string, tentar converter
    const str = String(value).trim();
    if (str.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      return str; // Já está no formato brasileiro
    }
    
    // Tentar outros formatos
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('pt-BR');
    }
    
    return str;
  }

  private isNumberLike(str: string): boolean {
    return /^[\d.,\s]+$/.test(str.trim());
  }

  private getCategoriaAtividade(atividade: string): string {
    const atividadeLower = atividade.toLowerCase();
    
    if (atividadeLower.includes('planejamento') || atividadeLower.includes('projeto')) return 'Planejamento';
    if (atividadeLower.includes('demolição') || atividadeLower.includes('demolicao')) return 'Demolição';
    if (atividadeLower.includes('estrutura') || atividadeLower.includes('concreto')) return 'Estrutura';
    if (atividadeLower.includes('alvenaria') || atividadeLower.includes('tijolo')) return 'Alvenaria';
    if (atividadeLower.includes('elétrica') || atividadeLower.includes('eletrica')) return 'Elétrica';
    if (atividadeLower.includes('hidráulica') || atividadeLower.includes('hidraulica')) return 'Hidráulica';
    if (atividadeLower.includes('acabamento') || atividadeLower.includes('pintura')) return 'Acabamento';
    if (atividadeLower.includes('finalização') || atividadeLower.includes('entrega')) return 'Finalização';
    
    return 'Execução';
  }

  /**
   * MÉTODO SIMPLES DE FALLBACK para Excel
   */
  private async readExcelSimple(fileData: ArrayBuffer, fileName: string): Promise<DocumentContent> {
    try {
      const XLSX = await this.loadXLSX();
      const workbook = XLSX.read(fileData, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      let content = `📊 Arquivo Excel: ${fileName}\n\n`;
      
      // Extrair as primeiras 10 linhas como exemplo
      data.slice(0, 10).forEach((row: any, idx: number) => {
        if (Array.isArray(row)) {
          content += `${idx + 1}: ${row.slice(0, 5).join(' | ')}\n`;
        }
      });
      
      if (data.length > 10) {
        content += `... e mais ${data.length - 10} linhas\n`;
      }
      
      return {
        fileName,
        type: 'excel',
        content,
        structured: {
          cronograma: [],
          orcamento: [],
          resumoOrcamento: { totalGeral: 0, categorias: {} },
          resumoCronograma: { totalAtividades: 0, periodoExecucao: { inicio: '', fim: '', duracaoTotal: 0 }, fases: {} }
        }
      };
    } catch (error) {
      return {
        fileName,
        type: 'excel',
        content: `❌ Erro ao ler o arquivo Excel: ${fileName}\n\nTente abrir o arquivo manualmente para verificar o conteúdo.`
      };
    }
  }

  /**
   * Lê conteúdo de Word usando Docx (BROWSER-COMPATIBLE)
   */
  private async readWord(fileData: ArrayBuffer, fileName: string): Promise<DocumentContent> {
    try {
      console.log('📄 Lendo Word com Docx.js:', fileName);

      // Usar Docx.js via CDN (compatível com browser)
      const docxLib = await this.loadDocxJS();
      
      // Converter ArrayBuffer para Uint8Array
      const docxData = new Uint8Array(fileData);
      
      // Carregar documento Word
      const doc = await docxLib.load(docxData);
      console.log('📊 Word carregado:', fileName, 'páginas:', doc.paragraphs.length);

      let fullText = '';
      
      // Extrair texto de todas as páginas
      doc.paragraphs.forEach((paragraph: any) => {
        fullText += paragraph.text + '\n';
      });

      console.log('✅ Word processado:', fileName, 'caracteres:', fullText.length);

      return {
        fileName,
        type: 'word',
        content: fullText
      };

    } catch (error) {
      return {
        fileName,
        type: 'word',
        content: `Erro ao ler o arquivo Word: ${fileName}`
      };
    }
  }

  /**
   * Carrega PDF.js via CDN
   */
  private async loadPDFJS(): Promise<any> {
    if (typeof window !== 'undefined' && (window as any)['pdfjsLib']) {
      return (window as any)['pdfjsLib'];
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.crossOrigin = 'anonymous';
    document.body.appendChild(script);
    return new Promise(resolve => {
      script.onload = () => {
        if (typeof window !== 'undefined') {
          (window as any)['pdfjsLib'] = (window as any)['pdfjsLib'] || (window as any)['pdfjsLib'];
          resolve((window as any)['pdfjsLib']);
        }
      };
    });
  }

  /**
   * Carrega SheetJS (XLSX) via CDN
   */
  private async loadXLSX(): Promise<any> {
    if (typeof window !== 'undefined' && (window as any)['XLSX']) {
      return (window as any)['XLSX'];
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.crossOrigin = 'anonymous';
    document.body.appendChild(script);
    return new Promise(resolve => {
      script.onload = () => {
        if (typeof window !== 'undefined') {
          resolve((window as any)['XLSX']);
        }
      };
    });
  }

  /**
   * Carrega Docx.js via CDN
   */
  private async loadDocxJS(): Promise<any> {
    if (typeof window !== 'undefined' && (window as any)['docx']) {
      return (window as any)['docx'];
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/docx/0.0.27/docx.min.js';
    script.crossOrigin = 'anonymous';
    document.body.appendChild(script);
    return new Promise(resolve => {
      script.onload = () => {
        if (typeof window !== 'undefined') {
          (window as any)['docx'] = (window as any)['docx'] || (window as any)['docx'];
          resolve((window as any)['docx']);
        }
      };
    });
  }

  /**
   * Extrai dados de cronograma do texto
   */
  private extractCronogramaFromText(text: string): CronogramaData[] {
    const cronograma: CronogramaData[] = [];
    const lines = text.split('\n');
    let currentActivity: CronogramaData | null = null;

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Identificar atividades
      if (trimmedLine.startsWith('=== PÁGINA')) {
        currentActivity = null; // Resetar para nova página
        continue;
      }

      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('• ')) {
        const activityMatch = trimmedLine.match(/•\s*(.*)/);
        if (activityMatch) {
          const activityName = activityMatch[1].trim();
          currentActivity = {
            atividade: activityName,
            dataInicio: '',
            dataFim: '',
            duracao: '',
            responsavel: '',
            progresso: '',
            categoria: ''
          };
          cronograma.push(currentActivity);
        }
      }

      // Extrair datas e outros detalhes
      if (currentActivity) {
        if (trimmedLine.includes('Data Início:')) {
          const dateMatch = trimmedLine.match(/Data Início:\s*(.*)/);
          if (dateMatch) {
            currentActivity.dataInicio = dateMatch[1].trim();
          }
        }
        if (trimmedLine.includes('Data Fim:')) {
          const dateMatch = trimmedLine.match(/Data Fim:\s*(.*)/);
          if (dateMatch) {
            currentActivity.dataFim = dateMatch[1].trim();
          }
        }
        if (trimmedLine.includes('Duração:')) {
          const durationMatch = trimmedLine.match(/Duração:\s*(.*)/);
          if (durationMatch) {
            currentActivity.duracao = durationMatch[1].trim();
          }
        }
        if (trimmedLine.includes('Responsável:')) {
          const responsibleMatch = trimmedLine.match(/Responsável:\s*(.*)/);
          if (responsibleMatch) {
            currentActivity.responsavel = responsibleMatch[1].trim();
          }
        }
        if (trimmedLine.includes('Progresso:')) {
          const progressMatch = trimmedLine.match(/Progresso:\s*(.*)/);
          if (progressMatch) {
            currentActivity.progresso = progressMatch[1].trim();
          }
        }
        if (trimmedLine.includes('Categoria:')) {
          const categoryMatch = trimmedLine.match(/Categoria:\s*(.*)/);
          if (categoryMatch) {
            currentActivity.categoria = categoryMatch[1].trim();
          }
        }
      }
    }
    return cronograma;
  }

  /**
   * ✅ PARSER ACOR - Extrai dados de orçamento do texto (MANTIDO DO PDF)
   */
  private extractOrcamentoFromText(text: string): OrcamentoData[] {
    const orcamento: OrcamentoData[] = [];
    
    console.log('📋 Total de caracteres no texto:', text.length);
    
    // ✅ PRÉ-PROCESSAMENTO: Quebrar linhas concatenadas
    const linhasProcessadas = this.preprocessOrcamentoText(text);
    
    let itensExtraidos = 0;

    linhasProcessadas.forEach((line) => {
      // 🎯 PADRÃO ACOR PRINCIPAL: CÓDIGO DESCRIÇÃO ✓ ✓ UNIDADE QTD MAT_UNIT MAT_TOTAL MO_UNIT MO_TOTAL TOTAL
      // Exemplo: "24.1 Pintura De Paredes Externas ✓ ✓ m² 50,00 18,00 900,00 45,00 2.250,00 3.150,00"
      
      // Regex mais flexível para capturar diferentes variações do símbolo ✓
      const acorPattern = /^(\d{1,2}(?:\.\d{1,2})?)\s+([A-ZÀ-Úa-zà-ú].+?)\s+[✓√v✔]\s*[✓√v✔]?\s*(m²|m³|pt|cj|unid|pç|kg|ton|Unid\.?)?\s+([\d.,]+)\s+([\d.,]+)\s+([\d.,]+)\s+([\d.,]+)\s+([\d.,]+)\s+([\d.,]+)\s*$/;
      
      const match = line.match(acorPattern);
      
      if (match) {
        const codigo = match[1].trim();
        const descricao = match[2].trim();
        const unidade = match[3] || 'unid';
        const quantidade = this.parseNumberACOR(match[4]);
        const valorMatUnit = this.parseNumberACOR(match[5]);
        const valorMatTotal = this.parseNumberACOR(match[6]);
        const valorMoTotal = this.parseNumberACOR(match[8]);
        const valorTotal = this.parseNumberACOR(match[9]);
        
        const categoria = this.categorizarItemACOR(codigo, descricao);
        
        const item: OrcamentoData = {
          item: codigo,
          descricao: descricao,
          unidade: unidade,
          quantidade: quantidade,
          valorUnitario: valorMatUnit,
          valorMaterial: valorMatTotal,
          valorMaoObra: valorMoTotal,
          valorTotal: valorTotal,
          categoria: categoria
        };
        
        orcamento.push(item);
        itensExtraidos++;
        
        return;
      }
      
      // 🎯 PADRÃO ALTERNATIVO: Para itens de administração sem material
      // Exemplo: "29.0 Administração de Obra ✓ ✓ Unid. 2,00 - 11.000,00 22.000,00 22.000,00"
      const adminPattern = /^(\d{1,2}(?:\.\d{1,2})?)\s+([A-ZÀ-Úa-zà-ú].+?)\s+[✓√v✔]\s*[✓√v✔]?\s*(Unid\.?|unid)?\s*([\d.,]+)?\s*-?\s*([\d.,]+)?\s+([\d.,]+)\s+([\d.,]+)\s*$/;
      
      const adminMatch = line.match(adminPattern);
      
      if (adminMatch) {
        const codigo = adminMatch[1].trim();
        const descricao = adminMatch[2].trim();
        const unidade = adminMatch[3] || 'unid';
        const quantidade = this.parseNumberACOR(adminMatch[4] || '1');
        const valorMaoObra = this.parseNumberACOR(adminMatch[6]);
        const valorTotal = this.parseNumberACOR(adminMatch[7]);
        
        const categoria = this.categorizarItemACOR(codigo, descricao);
        
        const item: OrcamentoData = {
          item: codigo,
          descricao: descricao,
          unidade: unidade,
          quantidade: quantidade,
          valorUnitario: 0,
          valorMaterial: 0,
          valorMaoObra: valorMaoObra,
          valorTotal: valorTotal,
          categoria: categoria
        };
        
        orcamento.push(item);
        itensExtraidos++;
        
        return;
      }
    });
    
    return orcamento;
  }

  /**
   * ✅ PRÉ-PROCESSAMENTO: Quebra texto concatenado em linhas individuais
   */
  private preprocessOrcamentoText(text: string): string[] {
    console.log('🔧 Iniciando pré-processamento do texto...');
    
    // Dividir por quebras de linha existentes
    let lines = text.split('\n');
    
    // Procurar linhas que contêm múltiplos itens concatenados
    const processedLines: string[] = [];
    
    lines.forEach((line, index) => {
      // Se a linha é muito longa e contém múltiplos códigos ACOR
      if (line.length > 200 && this.hasMultipleACORCodes(line)) {
        console.log(`🔄 Processando linha concatenada ${index + 1} (${line.length} caracteres)`);
        
        // Quebrar por códigos ACOR
        const splitLines = this.splitByACORCodes(line);
        processedLines.push(...splitLines);
        
      } else {
        // Manter linha como está
        if (line.trim().length > 0) {
          processedLines.push(line.trim());
        }
      }
    });
    
    console.log(`📋 Pré-processamento concluído: ${lines.length} → ${processedLines.length} linhas`);
    return processedLines;
  }

  /**
   * ✅ HELPER: Verifica se linha contém múltiplos códigos ACOR
   */
  private hasMultipleACORCodes(line: string): boolean {
    // Buscar padrões como "24.1", "19.5", "29.0", etc.
    const codeMatches = line.match(/\d{1,2}\.\d{1,2}/g);
    return codeMatches ? codeMatches.length > 1 : false;
  }

  /**
   * ✅ HELPER: Divide linha por códigos ACOR
   */
  private splitByACORCodes(line: string): string[] {
    const splitLines: string[] = [];
    
    // Encontrar todas as posições dos códigos ACOR
    const codePattern = /(\d{1,2}\.\d{1,2})\s+([A-ZÀ-Úa-zà-ú])/g;
    const matches: Array<{index: number, code: string}> = [];
    
    let match;
    while ((match = codePattern.exec(line)) !== null) {
      matches.push({
        index: match.index,
        code: match[1]
      });
    }
    
    if (matches.length <= 1) {
      return [line]; // Não há nada para dividir
    }
    
    // Dividir a linha pelos códigos encontrados
    for (let i = 0; i < matches.length; i++) {
      const startIndex = matches[i].index;
      const endIndex = i < matches.length - 1 ? matches[i + 1].index : line.length;
      
      const itemLine = line.substring(startIndex, endIndex).trim();
      if (itemLine.length > 0) {
        splitLines.push(itemLine);
      }
    }
    
    return splitLines;
  }

  /**
   * ✅ PARSER HELPER: Converte string brasileira para number
   */
  private parseNumberACOR(str: string): number {
    if (!str || str === '-') return 0;
    
    // Remove pontos (separador de milhar) e converte vírgula para ponto decimal
    const cleaned = str.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    
    return isNaN(num) ? 0 : num;
  }

  /**
   * ✅ CRIA RESUMO DE ORÇAMENTO ACOR
   */
  private createResumoOrcamento(orcamentoData: OrcamentoData[]): ResumoOrcamento {
    const resumo: ResumoOrcamento = {
      totalGeral: 0,
      categorias: {}
    };

    console.log('📊 Criando resumo de orçamento ACOR...');

    orcamentoData.forEach(item => {
      const categoria = item.categoria || 'outros';
      
      // Inicializar categoria se não existir
      if (!resumo.categorias[categoria]) {
        resumo.categorias[categoria] = {
          quantidade: 0,
          valorMaterial: 0,
          valorMaoObra: 0,
          valorTotal: 0,
          itens: 0,
          detalhes: []
        };
      }
      
      // Agregar valores
      resumo.categorias[categoria].quantidade += item.quantidade || 0;
      resumo.categorias[categoria].valorMaterial += item.valorMaterial || 0;
      resumo.categorias[categoria].valorMaoObra += item.valorMaoObra || 0;
      resumo.categorias[categoria].valorTotal += item.valorTotal || 0;
      resumo.categorias[categoria].itens += 1;
      
      // Adicionar detalhes do item
      resumo.categorias[categoria].detalhes.push({
        codigo: item.item,
        descricao: item.descricao,
        quantidade: item.quantidade,
        valorTotal: item.valorTotal,
        unidade: item.unidade
      });
      
      // Somar ao total geral
      resumo.totalGeral += item.valorTotal || 0;
    });

    // 🎯 LOG de categorias encontradas
    console.log('📋 Categorias criadas:');
    Object.entries(resumo.categorias).forEach(([cat, dados]) => {
      console.log(`  • ${cat.toUpperCase()}: R$ ${dados.valorTotal.toLocaleString('pt-BR')} (${dados.itens} itens)`);
    });
    
    console.log(`💰 Total geral: R$ ${resumo.totalGeral.toLocaleString('pt-BR')}`);

    return resumo;
  }

  /**
   * ✅ CATEGORIZAÇÃO ACOR: Baseada em códigos + descrição
   */
  private categorizarItemACOR(codigo: string, descricao: string): string {
    const desc = descricao.toLowerCase();
    const codigoNum = parseFloat(codigo);
    
    // 🎯 CATEGORIZAÇÃO POR CÓDIGO ACOR
    const categoriasPorCodigo: { [key: string]: string } = {
      '1': 'servicos_iniciais',
      '4': 'demolicoes', 
      '5': 'estrutura',
      '7': 'alvenaria',
      '8': 'drywall',
      '11': 'eletrica',
      '12': 'luminarias', 
      '13': 'logica_telecom',
      '14': 'hidrossanitaria',
      '15': 'loucas_metais',
      '16': 'climatizacao',
      '18': 'contrapiso',
      '19': 'forro',
      '20': 'pisos',
      '21': 'esquadrias_madeira',
      '22': 'esquadrias_vidro',
      '24': 'pintura',
      '27': 'marcenaria',
      '28': 'servicos_complementares',
      '29': 'administracao'
    };
    
    // Categoria principal pelo código
    const codigoPrincipal = Math.floor(codigoNum).toString();
    let categoria = categoriasPorCodigo[codigoPrincipal] || 'outros';
    
    // 🎯 REGRAS ESPECIAIS para itens relacionados
    
    // Pintura DE TETOS E FORROS → categoria "forro" (mais específico)
    if (categoria === 'pintura' && (desc.includes('teto') || desc.includes('forro'))) {
      categoria = 'forro';
    }
    
    return categoria;
  }

  /**
   * Cria resumo de cronograma
   */
  private createResumoCronograma(cronogramaData: CronogramaData[]): ResumoCronograma {
    const resumo: ResumoCronograma = {
      totalAtividades: 0,
      periodoExecucao: {
        inicio: '',
        fim: '',
        duracaoTotal: 0
      },
      fases: {}
    };

    cronogramaData.forEach(activity => {
      resumo.totalAtividades++;
      if (!resumo.periodoExecucao.inicio) {
        resumo.periodoExecucao.inicio = activity.dataInicio;
      }
      if (!resumo.periodoExecucao.fim) {
        resumo.periodoExecucao.fim = activity.dataFim;
      }
      resumo.periodoExecucao.duracaoTotal += this.calculateDurationInDays(activity.dataInicio, activity.dataFim);

      // Adicionar atividades às fases
      const phaseName = this.getPhaseName(activity.atividade);
      if (!resumo.fases[phaseName]) {
        resumo.fases[phaseName] = {
          atividades: 0,
          inicio: '',
          fim: ''
        };
      }
      resumo.fases[phaseName].atividades++;
      if (!resumo.fases[phaseName].inicio) {
        resumo.fases[phaseName].inicio = activity.dataInicio;
      }
      if (!resumo.fases[phaseName].fim) {
        resumo.fases[phaseName].fim = activity.dataFim;
      }
    });

    return resumo;
  }

  /**
   * Calcula a duração em dias entre duas datas
   */
  private calculateDurationInDays(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Tenta identificar a fase de uma atividade
   */
  private getPhaseName(activityName: string): string {
    const lowerCaseName = activityName.toLowerCase();
    if (lowerCaseName.includes('planejamento') || lowerCaseName.includes('preparatório') || lowerCaseName.includes('projeto')) {
      return 'Planejamento';
    }
    if (lowerCaseName.includes('execução') || lowerCaseName.includes('obra') || lowerCaseName.includes('construção')) {
      return 'Execução';
    }
    if (lowerCaseName.includes('conclusão') || lowerCaseName.includes('finalização') || lowerCaseName.includes('encerramento')) {
      return 'Conclusão';
    }
    return 'Outro';
  }
}

export const fileReaderService = new FileReaderService();